import { PencilLine } from 'lucide-react';
import { capitalize } from 'lodash';
import { useNavigate } from 'react-router';
import { useSuspenseQuery } from '@apollo/client/react';
import { Fragment, useMemo } from 'react';

import { useSoftwareEscrow } from 'src/context/software-escrow';
import {
  EscrowPartyTypeEnum,
  type SuggestedChangeChangeTypeEnum,
  type IBeneficiary,
  type IDepositor,
} from 'src/graphql/schema';
import { SuggestedChangesDocument } from 'src/graphql/actions/suggestChanges/suggestedChanges.query';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

import { InvitationView, PartyView, Representatives } from './';

import { RequestChangesTooltip } from '../../RequestChanges';

import type { ISuggestedChangesQuery } from 'src/graphql/actions/suggestChanges/suggestedChanges.query';

type RequestChangesActionsProps = {
  partyType: EscrowPartyTypeEnum;
  suggestedChange: ISuggestedChangesQuery['suggestedChanges']['nodes'][0];
};

const RequestChangesActions = ({ partyType, suggestedChange }: RequestChangesActionsProps) => {
  const {
    softwareEscrow: { id, policy },
  } = useSoftwareEscrow();
  const navigate = useNavigate();

  const onRequestChanges = () => navigate(`/software-escrows/${id}/request-changes/${partyType}`);

  const actions = useMemo(
    () => [
      {
        id: 'requestChanges',
        component: (
          <Button variant="outline" onClick={onRequestChanges}>
            <PencilLine />
            Request changes
          </Button>
        ),
        condition: policy[`create${capitalize(partyType)}SuggestedChange`],
      },
      {
        id: 'requestChanges',
        component: <RequestChangesTooltip />,
        condition: suggestedChange,
      },
    ],
    [partyType, policy, suggestedChange],
  );

  const actionsFiltered = actions.filter((action) => action.condition);

  if (!actionsFiltered.length) return null;

  return (
    <div className="flex justify-end gap-3 mb-8">
      {actionsFiltered.map(({ id, component }) => (
        <Fragment key={id}>{component}</Fragment>
      ))}
    </div>
  );
};

type Props = {
  partyType: EscrowPartyTypeEnum;
};

const SettingsPartyContainer = ({ partyType }: Props) => {
  const { softwareEscrow } = useSoftwareEscrow();
  const {
    data: { suggestedChanges },
  } = useSuspenseQuery(SuggestedChangesDocument, {
    fetchPolicy: 'cache-and-network',
    variables: {
      escrowId: softwareEscrow.id,
      changeType: `${capitalize(partyType)}SuggestedChange` as SuggestedChangeChangeTypeEnum,
    },
  });

  const party = softwareEscrow[partyType] as IDepositor | IBeneficiary;

  if (!party) return <InvitationView partyType={partyType} />;

  return (
    <div className="space-y-8">
      <RequestChangesActions partyType={partyType} suggestedChange={suggestedChanges.nodes[0]} />
      <PartyView partyType={partyType} />
      <Separator />
      <Representatives partyType={partyType} />
    </div>
  );
};

export default SettingsPartyContainer;
